import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import { useHistory } from "react-router-dom";

import useAccountMetadata from "@/hooks/useAccountMetadata";
import { isAccountInHIPAAMode, isAccountInHIPAAModeV2 } from "@/util";
import { destinationHasPropertyMetadata } from "@/destinations/DestinationPropertyMetadata";
import { Button } from "@/design-system/components/Button/Button";

interface DestinationConfigRowProps {
  name: string;
  textColor?: string;
  labelText?: string;
  labelStyles?: React.CSSProperties;
  onOpenConfigModal?: () => void;
  disabled?: boolean;
  buttonText?: string;
  buttonComponent?: React.ReactNode;
  className?: string;
}

export function DestinationConfigRow({
  name,
  textColor,
  labelText,
  labelStyles = {},
  onOpenConfigModal,
  disabled = false,
  buttonText = "Configure",
  className,
  buttonComponent = null,
}: DestinationConfigRowProps) {
  return (
    <div
      data-testid="destination-config-row"
      className={clsx(
        "flex items-center justify-between p-4 first:rounded-t-md last:rounded-b-md",
        disabled ? "bg-gray-50" : "",
        className && className,
      )}
    >
      <div>
        <h5 className="text-sm font-bold">{name}</h5>
        {!disabled && labelText && (
          <h4
            className="text-textSubtle"
            style={{ color: textColor, ...labelStyles }}
          >
            {labelText}
          </h4>
        )}
      </div>
      <div className="text-right">
        {!disabled &&
          (buttonComponent ? (
            buttonComponent
          ) : (
            <Button onClick={onOpenConfigModal} variant="link">
              {buttonText}
            </Button>
          ))}
      </div>
    </div>
  );
}

export function DestinationHIPAARow({
  onOpenConfigModal,
  disabled = false,
  configuredDestination,
}) {
  const accountMetadata = useAccountMetadata();

  if (!isAccountInHIPAAMode(accountMetadata)) {
    return null;
  }
  let labelConfigurationText = configuredDestination.has_baa
    ? "Allowing PHI"
    : "Restricting PHI";

  return (
    <DestinationConfigRow
      name="HIPAA Settings"
      labelText={labelConfigurationText}
      onOpenConfigModal={onOpenConfigModal}
      buttonText="Configure"
      disabled={disabled}
    />
  );
}

DestinationHIPAARow.propTypes = {
  onOpenConfigModal: PropTypes.func.isRequired,
  configuredDestination: PropTypes.object.isRequired,
};

export function DestinationPHIAllowlistConfigRow({
  destination,
  configuredDestination,
  disabled,
}) {
  const accountMetadata = useAccountMetadata();
  const history = useHistory();

  const onConfigure = () => {
    history.push(
      destination.configPageUrl() + "/phiallowlist/" + configuredDestination.id,
    );
  };

  if (!isAccountInHIPAAModeV2(accountMetadata)) {
    return null;
  }

  if (!destination.supportsServerSide) {
    return null;
  }

  let rowLabel = "HIPAA Allow List";
  if (destinationHasPropertyMetadata(destination.key)) {
    rowLabel = "Advanced: " + rowLabel;
  }

  return (
    <DestinationConfigRow
      name={rowLabel}
      onOpenConfigModal={onConfigure}
      buttonText="Configure"
      disabled={disabled || configuredDestination.has_baa}
    />
  );
}

DestinationPHIAllowlistConfigRow.propTypes = {
  destination: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

export function DestinationConnectionModeRow({
  onOpenConfigModal,
  disabled = false,
  configuredDestination,
}) {
  let connectionModeLabel = "None specified";

  if (configuredDestination.connection_mode === "client") {
    connectionModeLabel = "Client-side";
  } else if (configuredDestination.connection_mode === "server") {
    connectionModeLabel = "Server-side";
  }
  return (
    <DestinationConfigRow
      name="Connection Mode Settings"
      labelText={connectionModeLabel}
      onOpenConfigModal={onOpenConfigModal}
      buttonText="Configure"
      disabled={disabled || configuredDestination.has_baa}
    />
  );
}

DestinationConnectionModeRow.propTypes = {
  onOpenConfigModal: PropTypes.func.isRequired,
  configuredDestination: PropTypes.object.isRequired,
};
